import * as React from "react";
import { FC, ReactNode } from "react";
import classNames from "classnames";
import { format } from "lib/globalize-util";
import theme from "styles/theme";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Grid from "@material-ui/core/Grid";
import makeStyles from "@material-ui/core/styles/makeStyles";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import { DATATEST } from "constants/integration";

interface IUploadProps {
    open: boolean;
    title: ReactNode;
    disabled?: boolean;
    disabledDelete?: boolean;
    disabledSave?: boolean;
    classes?: IDialogClasses;
    onClose(): void;
    onSave?(): void;
    onDelete?(): void;
    customActions?: React.ReactNode;
}

export interface IDialogClasses extends IHeaderClasses {
    dialog?: string;
    paper?: string;
    content?: string;
    actions?: string;
    footer?: IDialogFooterClasses;
}

export const MediaDialog: FC<IUploadProps> = (props) => {
    const { open, title, onClose, onSave, onDelete, classes = {}, children, customActions } = props;
    const { disabled, disabledDelete, disabledSave } = props;
    const inner = useStyles({});
    return (
        <Dialog
            open={open}
            onClose={onClose}
            classes={{ root: classNames(inner.dialog, classes.dialog), paper: classNames(inner.paper, classes.paper) }}
        >
            <DialogHeader title={title} onClose={onClose} classes={classes} />
            <DialogContent className={classNames(inner.content, classes.content)}>{children}</DialogContent>
            <DialogFooter
                className={classes.actions}
                onSave={onSave}
                onClose={onClose}
                onDelete={onDelete}
                disabled={disabled}
                disabledDelete={disabledDelete}
                disabledSave={disabledSave}
                classes={classes.footer}
                customActions={customActions}
            />
        </Dialog>
    );
};

interface IDialogTitleProps {
    title: ReactNode;
    onClose: () => void;
    className?: string;
    classes?: IHeaderClasses;
}

interface IHeaderClasses {
    title?: string;
    close?: string;
    iconClose?: string;
}

export const DialogHeader: FC<IDialogTitleProps> = (props) => {
    const { title, onClose, className, classes = {} } = props;
    const inner = useTitleStyles({});
    return (
        <DialogTitle
            className={classNames(inner.dialogTitle, className, classes.title)}
            data-test={DATATEST.MEDIA_DIALOG.DIALOG_TITLE}
            disableTypography={true}
        >
            <span>{title}</span>
            <IconButton className={classNames(inner.close, classes.close)} aria-label="close" onClick={onClose}>
                <CloseIcon className={classNames(inner.iconClose, classes.iconClose)} />
            </IconButton>
        </DialogTitle>
    );
};

interface IDialogFooterClasses {
    closeButton?: string;
    deleteButton?: string;
    saveButton?: string;
}

interface IDialogFooterProps {
    onSave?: () => void;
    saveText?: string;
    closeText?: string;
    deleteText?: string;
    onClose?: () => void;
    onDelete?: () => void;
    disabled?: boolean;
    disabledSave?: boolean;
    disabledDelete?: boolean;
    className?: string;
    classes?: IDialogFooterClasses;
    customActions?: React.ReactNode;
}

export const DialogFooter: FC<IDialogFooterProps> = (props) => {
    const {
        onSave,
        onClose,
        onDelete,
        disabled,
        disabledDelete,
        disabledSave,
        className,
        classes = {},
        customActions,
    } = props;
    const { saveText = format("Save"), deleteText = format("Delete"), closeText = format("Close") } = props;
    const inner = useFooterStyles({});
    return (
        <DialogActions className={classNames(inner.actions, className)}>
            <div className={inner.customActions}>{customActions}</div>
            <Grid className={inner.grid} container={true}>
                {onDelete && (
                    <Grid className={inner.buttonGrid} item={true} xs={true}>
                        <Button
                            className={classNames(inner.button, classes?.deleteButton)}
                            data-test={DATATEST.MEDIA_DIALOG.DELETE_BTN}
                            disabled={disabled || disabledDelete}
                            onClick={onDelete}
                        >
                            {deleteText}
                        </Button>
                    </Grid>
                )}
                {onClose && (
                    <Grid className={inner.buttonGrid} item={true}>
                        <Button
                            className={classNames(inner.button, classes?.closeButton)}
                            data-test={DATATEST.MEDIA_DIALOG.CLOSE_BTN}
                            onClick={onClose}
                        >
                            {closeText}
                        </Button>
                    </Grid>
                )}
                {onSave && (
                    <Grid className={inner.buttonGrid} item={true}>
                        <Button
                            className={classNames(inner.button, inner.save, classes?.saveButton)}
                            data-test={DATATEST.MEDIA_DIALOG.SAVE_BTN}
                            disabled={disabled || disabledSave}
                            onClick={onSave}
                        >
                            {saveText}
                        </Button>
                    </Grid>
                )}
            </Grid>
        </DialogActions>
    );
};

const useTitleStyles = makeStyles({
    dialogTitle: {
        fontSize: 18,
        fontWeight: "bold",
        borderBottom: `1px solid ${theme.palette.lightGrey}`,
    },
    close: {
        position: "absolute",
        top: 5,
        right: 7,
    },
    iconClose: {
        width: 24,
        height: 24,
        color: theme.palette.darkestGrey,
    },
});

const useFooterStyles = makeStyles({
    actions: {
        justifyContent: "space-between",
        borderTop: `1px solid ${theme.palette.lightGrey}`,
        padding: "10px",
    },
    grid: {
        width: "auto",
        justifyContent: "flex-end",
    },
    customActions: {
        paddingLeft: 10,
    },
    buttonGrid: {
        margin: "5px",
        ["@media(max-width:450px)"]: {
            flexBasis: "100%",
        },
    },
    button: {
        height: 40,
        padding: "0 32px",
        fontSize: "14px",
        fontFamily: "inherit",
        color: theme.palette.darkGrey,
        textTransform: "none",
        whiteSpace: "nowrap",
        border: `1px solid ${theme.palette.lightGrey}`,
        borderRadius: 20,
        "@media(max-width:450px)": {
            width: "100%",
        },
    },
    save: {
        border: `1px solid ${theme.palette.accent1Color}`,
        color: "#fff",
        backgroundColor: theme.palette.accent1Color,
        "&:hover": {
            backgroundColor: "#2dbeb4",
        },
        "&:disabled": {
            color: "#fff",
            backgroundColor: "#8ccac6",
        },
    },
});

const useStyles = makeStyles({
    dialog: {
        zIndex: "1500 !important" as any,
    },
    paper: {
        maxWidth: 850,
    },
    content: {
        position: "relative",
        paddingTop: 20,
        paddingBottom: 20,
    },
});

export default MediaDialog;
